import { Base, variables } from '@bitrise/bitkit';
import {
  SeriesPointXY,
  useGetNearestPoint,
  Viz,
  VizHTML,
  VizHTMLArtboard,
  VizSVG,
  VizSVGAxisX,
  VizSVGAxisY,
  VizSVGGridLinesAxisY,
  VizSVGLine,
} from '@bitrise/bitviz';
import { OverviewMetric } from '@trace/types';
import max from 'lodash.max';
import React, { FunctionComponent, PointerEvent, useState } from 'react';
import { useParams } from 'react-router';

import getFormattedValue from '../../../utils/metric/getFormattedValue';
import { trackEvent } from '../../../utils/tracking';
import MessageFailing from '../../ViewVisualisations/MessageFailing';
import MessageFetching from '../../ViewVisualisations/MessageFetching';
import OverviewCard from '../OverviewCard/OverviewCard';
import OverviewTitle from '../OverviewCard/OverviewTitle';
import OverviewComparison from '../OverviewComparison/OverviewComparison';
import OverviewTooltip from '../OverviewTooltip/OverviewTooltip';
import OverviewTooltipComparison from '../OverviewTooltip/OverviewTooltipComparison';

export interface Props {
  data?: OverviewMetric;
  isError: boolean;
  isFetching: boolean;
  refetch: () => void;
  title: string;
  xAxisLabels: [string, number][];
}

const OverviewLatency: FunctionComponent<Props> = ({
  data,
  isError,
  isFetching,
  refetch,
  title,
  xAxisLabels,
}) => {
  const { appId } = useParams<{ appId: string }>();
  const [focusPoint, setFocusPoint] = useState<null | SeriesPointXY>(null);
  const yMax = max(data?.app) || 100;

  const getNearestPoint = useGetNearestPoint(
    data?.app.map((y, x) => ({ x, y })),
    { axis: 'x' },
  );

  const onPointerMove = (_: PointerEvent, point?: SeriesPointXY) => {
    if (point && point.x !== null) {
      const nearestPoint = getNearestPoint(point);

      if (nearestPoint && nearestPoint.x !== null) {
        setFocusPoint(nearestPoint);
        return;
      }
    }

    setFocusPoint(null);
  };

  const onCardButtonClick = () => {
    trackEvent('Overview.WidgetDetails', { widget: 'performance' });
  };

  return (
    <OverviewCard
      toPath={`/${appId}/appPerformance`}
      toText="Performance"
      onCardButtonClick={onCardButtonClick}
    >
      <OverviewTitle direction="vertical" title={title}>
        <OverviewComparison
          align="middle"
          data={data}
          isFetching={isFetching}
          positiveDelta={-1}
          small
          unit="ms"
        />
      </OverviewTitle>

      <Viz
        height={200}
        paddingBottom={22}
        paddingLeft={34}
        paddingTop={22}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerMove}
        xDomain={xAxisLabels.map((_, x) => x)}
        xDomainScale="band"
        xDomainPaddingOuter={0.5}
        yDomain={[0, yMax]}
      >
        <VizHTML>
          <VizHTMLArtboard>
            <Base borderRadius="x2" backgroundColor="gray-1" width="100%" height="100%" />
          </VizHTMLArtboard>
        </VizHTML>

        <VizSVG offsetArtboardClipMaskTop={4}>
          <VizSVGAxisX
            fill={variables.colorGray6}
            ticksOnly
            tickLineLength={0.001}
            format={(x) => xAxisLabels[x][0]}
          />

          <VizSVGAxisY
            fill={variables.colorGray6}
            flip
            format={(v) => getFormattedValue('ms', v)}
            tickLineLength={0.001}
            tickCount={3}
            ticksOnly
          />

          {!isError && !isFetching && (
            <VizSVGGridLinesAxisY
              animate
              stroke={variables.colorGray5}
              strokeDasharray="2 6"
              style={{ pointerEvents: 'none' }}
              tickCount={3}
            />
          )}

          {data?.app.map((y, x) => (
            <VizSVGLine
              // eslint-disable-next-line react/no-array-index-key
              key={x}
              animate
              clipToArtboard
              originY={0}
              series={[
                { x, y: 0 },
                { x, y },
              ]}
              stroke={variables.colorGrape5}
              strokeLinecap="round"
              strokeWidth={4}
            />
          ))}
        </VizSVG>

        <VizHTML>
          <VizHTMLArtboard>
            <MessageFetching fetching={isFetching} />
            <MessageFailing failing={isError} onFetch={refetch} />
          </VizHTMLArtboard>
        </VizHTML>

        {!!focusPoint && focusPoint.x !== null && (
          <OverviewTooltip point={focusPoint} timestamp={xAxisLabels[focusPoint.x][1]}>
            <OverviewTooltipComparison
              currValue={focusPoint.y}
              prevValue={data?.app[focusPoint.x - 1] ? data?.app[focusPoint.x - 1] : null}
              unit="ms"
            />
          </OverviewTooltip>
        )}
      </Viz>
    </OverviewCard>
  );
};

export default OverviewLatency;
